import { FunctionComponent, memo } from "react";
import { IProps } from "./select-menu-header.types";
import { Tooltip } from "@edgetier/components";
import { ActionIcon, Group, Text } from "@mantine/core";

import { faChevronDoubleRight } from "@fortawesome/pro-solid-svg-icons/faChevronDoubleRight";
import { faChevronDoubleLeft } from "@fortawesome/pro-solid-svg-icons/faChevronDoubleLeft";
import { Icon } from "../../../icon";
import { Badge } from "../../../badge";

/**
 * Component for displaying a message in a select menu, accompanied by an info icon.
 * @param {object} props - The props for the SelectMenuMessage component.
 * @param {string} props.message - The message to display in the select menu.
 * @returns {JSX.Element} SelectMenuMessage component.
 */
const SelectMenuHeader: FunctionComponent<IProps> = ({
    notSelectedItems,
    hideBulkActionSelectAll,
    selectAllBulkActionTooltipText,
    isSelectAllDisabled,
    onSelectAll,
    hideBulkActionDeselectAll,
    deselectAllDisabledTooltip,
    isDeselectAllDisabled,
    onSelectNone,
    itemLimit,
    selectedItems,
}) => {
    return (
        <Group justify="space-between" gap="sm" align="center" p="md">
            <Group flex={1} align="center" justify="space-between">
                <Text size="md">Not selected ({notSelectedItems.length})</Text>
                <Group align="center" justify="space-between">
                    {!hideBulkActionSelectAll && (
                        <Tooltip
                            content={selectAllBulkActionTooltipText}
                            useArrow
                            disableTooltip={!isSelectAllDisabled || !selectAllBulkActionTooltipText}
                        >
                            <ActionIcon
                                variant="default"
                                size="lg"
                                color="gray.6" // TODO: Watchtower 2.0 - This would be replaced with the renamed token `GRAY` created by the designers.
                                disabled={isSelectAllDisabled}
                                onClick={onSelectAll}
                                aria-label="Select all"
                                data-testid="add-all-button"
                            >
                                <Icon size="xs" icon={faChevronDoubleRight} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            </Group>
            <Group flex={1} align="center" justify="space-between">
                <Group align="center" justify="space-between">
                    {!hideBulkActionDeselectAll && (
                        <Tooltip
                            content={deselectAllDisabledTooltip}
                            useArrow
                            disableTooltip={!isDeselectAllDisabled || !deselectAllDisabledTooltip}
                        >
                            <ActionIcon
                                variant="default"
                                size="lg"
                                color="gray.6" // TODO: Watchtower 2.0 - This would be replaced with the renamed token `GRAY` created by the designers.
                                disabled={isDeselectAllDisabled}
                                onClick={onSelectNone}
                                aria-label="Select none"
                                data-testid="remove-all-button"
                            >
                                <Icon size="xs" icon={faChevronDoubleLeft} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
                <Group align="center" gap="sm" justify="space-between">
                    {itemLimit && (
                        <Badge
                            color={
                                itemLimit === selectedItems.length
                                    ? "var(--mantine-color-error-light-hover)"
                                    : "var(--mantine-color-warning-light-hover)"
                            }
                        >
                            <Text
                                c={
                                    itemLimit === selectedItems.length
                                        ? "var(--mantine-color-text-error-fade-1)"
                                        : "var(--mantine-color-text-warning-fade-1)"
                                }
                                span
                                size="xs"
                            >
                                Max {itemLimit}
                            </Text>
                        </Badge>
                    )}
                    <Text size="md">Selected ({selectedItems.length})</Text>
                </Group>
            </Group>
        </Group>
    );
};

export default memo(SelectMenuHeader);
